// ============================================================================
// VISUALIZER CANVAS — Polymorphic Layout Controller
//
// Architecture:
//   VisualizerCanvas
//     └─ VisualizerErrorBoundary        (class-based, catches render throws)
//         └─ VisualizerShell            (keyed on algorithmId → triggers full
//             └─ <resolved visualizer>   teardown + remount on algo switch)
//
// Routing is two-phase and mutually exclusive:
//   Phase 1 — CATEGORY TABLE: maps the algorithm's declared category string
//              to a canonical VisualizerKind. This is the authoritative source
//              of truth and never falls through to payload sniffing.
//   Phase 2 — PAYLOAD RECONCILIATION: if the live payload type contradicts the
//              category decision, a mismatch diagnostic is shown instead of
//              silently rendering the wrong widget or crashing.
//
// Teardown guarantee:
//   React's key mechanism on VisualizerShell ensures that switching algorithmId
//   unmounts the old shell (running all useEffect cleanups — RAF cancellations,
//   ResizeObserver disconnects, canvas clears) before mounting the new one.
//   No ghost frames or leaked timers can survive an algorithm switch.
// ============================================================================

import React, { Component, Suspense, lazy, memo } from 'react';
import { AlertTriangle, Cpu } from 'lucide-react';
import type { ExecutionStep } from '../../../core/types/rust-engine-interfaces';
import './roundRectPolyfill';

const SortingVisualizer = lazy(() => import('./SortingVisualizer').then((m) => ({ default: m.SortingVisualizer })));
const GraphVisualizer = lazy(() => import('./GraphVisualizer').then((m) => ({ default: m.GraphVisualizer })));
const TreeVisualizer = lazy(() => import('./TreeVisualizer').then((m) => ({ default: m.TreeVisualizer })));
const MatrixVisualizer = lazy(() => import('./MatrixVisualizer').then((m) => ({ default: m.MatrixVisualizer })));
const StringVisualizer = lazy(() => import('./StringVisualizer').then((m) => ({ default: m.StringVisualizer })));
const GenericElementVisualizer = lazy(() => import('./GenericElementVisualizer').then((m) => ({ default: m.GenericElementVisualizer })));

const VisualizerSuspense: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense
    fallback={
      <div className="flex h-full items-center justify-center text-[11px] text-zinc-500">
        <Cpu className="w-4 h-4 mr-2 animate-pulse" />
        Loading visualizer…
      </div>
    }
  >
    {children}
  </Suspense>
);

// ── Canonical visualizer kinds ────────────────────────────────────────────────

type VisualizerKind =
  | 'sorting'    // bar-chart canvas: sorting, search, greedy, math, number-theory
  | 'graph'      // SVG force/circular layout: graph algorithms
  | 'tree'       // SVG hierarchical layout: data-structures, tree algorithms
  | 'matrix'     // CSS grid DP table: dp, backtracking
  | 'string'     // canvas character cells: string/pattern matching
  | 'generic';   // SVG generic elements: geometry, sat, unknown

// ── Acceptable payload types for each visualizer kind ────────────────────────
// Defines which payload.type values are valid inputs for each visualizer.
// Any payload type NOT listed here for a given kind triggers the mismatch UI.

const ACCEPTED_PAYLOADS: Record<VisualizerKind, ReadonlySet<string>> = {
  sorting:  new Set(['array', 'generic']),
  graph:    new Set(['graph', 'generic']),
  tree:     new Set(['tree', 'array', 'generic']),
  matrix:   new Set(['matrix', 'array', 'generic']),
  string:   new Set(['string', 'array', 'generic']),
  generic:  new Set(['generic', 'array', 'graph', 'matrix', 'string', 'tree']),
};

// ── Category → VisualizerKind routing table ───────────────────────────────────
// Normalized (lowercase, trimmed) category strings → canonical kind.
// This is the single source of truth. No runtime branching anywhere else.

const CATEGORY_ROUTE: Record<string, VisualizerKind> = {
  // Sorting & sequential search
  'sorting':            'sorting',
  'search':             'sorting',
  'greedy':             'sorting',
  'hybrid':             'sorting',
  'math':               'sorting',
  'number theory':      'sorting',
  'number-theory':      'sorting',

  // Graph algorithms (weighted, flow, traversal, connectivity)
  'graph':              'graph',
  'graphs':             'graph',

  // Data structures (BST, heaps, AVL, RB-tree, etc.)
  'data structures':    'tree',
  'data-structures':    'tree',
  'tree':               'tree',
  'trees':              'tree',

  // Dynamic programming & constraint satisfaction
  'dp':                 'matrix',
  'dynamic programming':'matrix',
  'dynamic-programming':'matrix',
  'backtracking':       'matrix',

  // String / pattern matching
  'string':             'string',
  'strings':            'string',
  'automaton':          'string',
  'compression':        'string',
  'cryptography':       'string',

  // Flow & network algorithms
  'flow':               'graph',
  'network':            'graph',
  'network-flow':       'graph',

  // Game / adversarial search
  'game':               'tree',
  'games':              'tree',
  'game theory':        'tree',
  'game-theory':        'tree',
  'minimax':            'tree',

  // Geometry, SAT, and anything unclassified
  'geometry':           'graph',
  'geometric':          'graph',
  'range tree':         'sorting',
  'sat':                'graph',
  'logic':              'graph',
  'other':              'generic',
  'uncategorized':      'generic',
};

// ── Payload-type-only fallback table ─────────────────────────────────────────
// Used when the algorithm category is unrecognized — resolve by payload shape
// rather than silently crashing or rendering a blank screen.

const PAYLOAD_ROUTE: Record<string, VisualizerKind> = {
  array:   'sorting',
  graph:   'graph',
  tree:    'tree',
  matrix:  'matrix',
  string:  'string',
  generic: 'generic',
};

// ── Per-algorithm-id routing overrides ───────────────────────────────────────
// Use sparingly — only for algorithms whose live payload type fundamentally
// differs from their category's canonical visualizer kind. This is the correct
// place to handle algorithmic exceptions without widening ACCEPTED_PAYLOADS.
//
// Key  : exact algorithmId string (lowercase, hyphenated)
// Value: the VisualizerKind that should handle this specific algorithm

const ALGORITHM_ROUTE_OVERRIDE: Record<string, VisualizerKind> = {
  // Hamiltonian Cycle is backtracking category but emits a graph payload.
  // Without override: backtracking→matrix, payload=graph→graph (auto-resolved).
  // Keep for clarity — makes the routing intent explicit.
  'hamiltonian-cycle':  'graph',
  'hamiltonian-path':   'graph',
  // Floyd-Warshall is graph category but emits an all-pairs distance matrix.
  // Without override: graph→graph, payload=matrix→matrix (auto-resolved).
  'floyd-warshall':     'matrix',
  // Huffman coding is greedy category but emits tree payloads.
  // Without override: greedy→sorting, payload=tree→tree (auto-resolved).
  'huffman-coding':     'tree',
  // Miller-Rabin is math category but emits matrix payload.
  // Without override: math→sorting, payload=matrix→matrix (auto-resolved).
  'miller-rabin':       'matrix',
  // Range Tree is graph category but emits tree payload.
  // Without override: graph→graph, payload=tree→tree (auto-resolved).
  'range-tree':         'tree',
  // Suffix Tree is string category but emits tree payload.
  // Without override: string→string, payload=tree→tree (auto-resolved).
  'suffix-tree':        'tree',

  // ── Data-structure algorithms that operate on a flat array ────────────────
  // Category "Data Structures" routes to the tree visualizer, and the tree
  // visualizer ACCEPTS array payloads — but it renders them as a BINARY HEAP
  // (node i at level ⌊log₂(i+1)⌋), which is wrong for these. Each of these has
  // a dedicated bar-chart overlay in SortingVisualizer (drawDsuOverlay,
  // drawFenwickArrows, drawSegmentTreeOverlay, drawSqrtBlockOverlay,
  // drawMoPointerOverlay, drawBloomFilterOverlay, drawSkipListLevels), so route
  // them to the sorting visualizer where that overlay actually runs.
  'disjoint-set-union': 'sorting',
  'fenwick-tree':       'sorting',
  'segment-tree':       'sorting',
  'skip-list':          'sorting',
  'bloom-filter':       'sorting',
  'sqrt-decomposition': 'sorting',
  'mo-algorithm':       'sorting',
};

// ── Routing resolution ────────────────────────────────────────────────────────

interface RouteResult {
  kind: VisualizerKind;
  /** True when payload type is inconsistent with the resolved kind */
  mismatch: boolean;
  resolvedBy: 'algorithm-override' | 'category' | 'payload' | 'fallback';
}

function resolveRoute(
  algorithmType: string,
  payloadType: string | undefined,
  algorithmId: string,
): RouteResult {
  // Phase 0 — per-algorithm-id override (highest priority)
  const normalizedId = algorithmId.toLowerCase().trim();
  const overrideKind = ALGORITHM_ROUTE_OVERRIDE[normalizedId];
  if (overrideKind !== undefined) {
    const mismatch =
      payloadType !== undefined &&
      !ACCEPTED_PAYLOADS[overrideKind].has(payloadType);
    return { kind: overrideKind, mismatch, resolvedBy: 'algorithm-override' };
  }

  const normalizedCategory = algorithmType.toLowerCase().trim();

  // Phase 1 — category routing, reconciled with the actual emitted payload.
  const categoryKind = CATEGORY_ROUTE[normalizedCategory];
  if (categoryKind !== undefined) {
    const acceptsPayload =
      payloadType === undefined ||
      ACCEPTED_PAYLOADS[categoryKind].has(payloadType);
    if (acceptsPayload) {
      return { kind: categoryKind, mismatch: false, resolvedBy: 'category' };
    }

    const payloadKind = PAYLOAD_ROUTE[payloadType];
    if (payloadKind !== undefined) {
      return { kind: payloadKind, mismatch: false, resolvedBy: 'payload' };
    }

    return { kind: categoryKind, mismatch: true, resolvedBy: 'category' };
  }

  // Phase 2 — payload-type routing (no recognized category)
  if (payloadType !== undefined) {
    const payloadKind = PAYLOAD_ROUTE[payloadType];
    if (payloadKind !== undefined) {
      return { kind: payloadKind, mismatch: false, resolvedBy: 'payload' };
    }
  }

  // Phase 3 — absolute fallback
  return { kind: 'generic', mismatch: false, resolvedBy: 'fallback' };
}

// ── Error boundary ────────────────────────────────────────────────────────────

interface ErrorBoundaryProps {
  algorithmId: string;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class VisualizerErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[VisualizerCanvas] Render error caught by boundary:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Clear error state when algorithmId changes — allow a fresh attempt
    if (prevProps.algorithmId !== this.props.algorithmId && this.state.error !== null) {
      this.setState({ error: null, errorInfo: null });
    }
  }

  override render(): React.ReactNode {
    if (this.state.error !== null) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full gap-4 px-6">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.22)' }}
          >
            <AlertTriangle className="w-5 h-5" style={{ color: '#f87171' }} />
          </div>
          <div className="text-center max-w-sm space-y-1.5">
            <p className="text-xs font-semibold" style={{ color: '#fca5a5' }}>
              Render Error
            </p>
            <p className="text-[11px] leading-relaxed" style={{ color: '#52525b' }}>
              {this.state.error.message || 'An unexpected error occurred while rendering the visualization.'}
            </p>
            <p className="text-[10px] font-mono" style={{ color: '#3f3f46' }}>
              {this.props.algorithmId}
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Payload mismatch diagnostic UI ────────────────────────────────────────────

interface MismatchDiagnosticProps {
  algorithmId: string;
  algorithmType: string;
  resolvedKind: VisualizerKind;
  actualPayloadType: string;
}

const MismatchDiagnostic: React.FC<MismatchDiagnosticProps> = ({
  algorithmId,
  algorithmType,
  resolvedKind,
  actualPayloadType,
}) => (
  <div className="flex flex-col items-center justify-center w-full h-full gap-3 px-8">
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.20)' }}
    >
      <AlertTriangle className="w-5 h-5" style={{ color: '#d97706' }} />
    </div>
    <div className="text-center max-w-xs space-y-2">
      <p className="text-xs font-semibold" style={{ color: '#fbbf24' }}>
        Payload Type Mismatch
      </p>
      <p className="text-[11px] leading-relaxed" style={{ color: '#52525b' }}>
        Algorithm category{' '}
        <span className="font-mono" style={{ color: '#71717a' }}>
          {algorithmType}
        </span>{' '}
        routes to the{' '}
        <span className="font-mono" style={{ color: '#71717a' }}>
          {resolvedKind}
        </span>{' '}
        visualizer, but the engine emitted a{' '}
        <span className="font-mono" style={{ color: '#f87171' }}>
          {actualPayloadType}
        </span>{' '}
        payload. Playback will continue; select a different step or re-run the algorithm.
      </p>
      <p className="text-[10px] font-mono" style={{ color: '#3f3f46' }}>{algorithmId}</p>
    </div>
  </div>
);

// ── No-step empty state ───────────────────────────────────────────────────────

const EmptyStepState: React.FC<{ algorithmId: string }> = ({ algorithmId }) => (
  <div className="flex flex-col items-center justify-center w-full h-full gap-3">
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
    >
      <Cpu className="w-5 h-5" style={{ color: '#3f3f46' }} />
    </div>
    <p className="text-[11px] font-mono" style={{ color: '#3f3f46' }}>
      {algorithmId ? `Awaiting first frame — ${algorithmId}` : 'No algorithm running'}
    </p>
  </div>
);

// ── Visualizer shell — owns key-based teardown ────────────────────────────────
// This component is keyed on `algorithmId` in VisualizerCanvas. React's key
// mechanism guarantees full unmount → mount when the ID changes, which runs all
// useEffect cleanups (RAF cancellations, ResizeObserver disconnects, canvas
// clears) in every child visualizer before the new one mounts. Zero ghost frames.

interface VisualizerShellProps {
  currentStep: ExecutionStep | null;
  kind: VisualizerKind;
  algorithmId: string;
  algorithmType: string;
  mismatch: boolean;
  allSteps?: ExecutionStep[];
  stepIndex?: number;
}

const VisualizerShell: React.FC<VisualizerShellProps> = ({
  currentStep,
  kind,
  algorithmId,
  algorithmType,
  mismatch,
  allSteps,
  stepIndex,
}) => {
  // Null step — show a neutral placeholder instead of passing null down
  if (currentStep === null) {
    return <EmptyStepState algorithmId={algorithmId} />;
  }

  const payloadType = currentStep.payload.type;

  // Payload/category mismatch — show diagnostic, do NOT attempt to render
  if (mismatch) {
    return (
      <MismatchDiagnostic
        algorithmId={algorithmId}
        algorithmType={algorithmType}
        resolvedKind={kind}
        actualPayloadType={payloadType}
      />
    );
  }

  // Dispatch to the correct visualizer
  let content: React.ReactNode;
  switch (kind) {
    case 'sorting':
      content = (
        <SortingVisualizer
          currentStep={currentStep}
          darkMode
          algorithmId={algorithmId}
          allSteps={allSteps}
          stepIndex={stepIndex}
        />
      );
      break;

    case 'graph':
      content = (
        <GraphVisualizer
          currentStep={currentStep}
          darkMode
          algorithmId={algorithmId}
        />
      );
      break;

    case 'tree':
      content = (
        <TreeVisualizer
          currentStep={currentStep}
          algorithmId={algorithmId}
        />
      );
      break;

    case 'matrix':
      content = <MatrixVisualizer currentStep={currentStep} algorithmId={algorithmId} />;
      break;

    case 'string':
      content = <StringVisualizer currentStep={currentStep} algorithmId={algorithmId} />;
      break;

    case 'generic':
    default:
      content = (
        <GenericElementVisualizer
          currentStep={currentStep}
          algorithmId={algorithmId}
        />
      );
      break;
  }

  return <VisualizerSuspense>{content}</VisualizerSuspense>;
};

// ── Public API ────────────────────────────────────────────────────────────────

export interface VisualizerCanvasProps {
  currentStep: ExecutionStep | null;
  algorithmType: string;
  algorithmId: string;
  allSteps?: ExecutionStep[];
  stepIndex?: number;
}

export const VisualizerCanvas: React.FC<VisualizerCanvasProps> = memo(({
  currentStep,
  algorithmType,
  algorithmId,
  allSteps,
  stepIndex,
}) => {
  const payloadType = currentStep?.payload?.type;
  const { kind, mismatch, resolvedBy } = resolveRoute(algorithmType, payloadType, algorithmId);

  if (import.meta.env.DEV && resolvedBy === 'fallback') {
    console.warn(
      `[VisualizerCanvas] "${algorithmId}" (${algorithmType}) — resolved via ${resolvedBy} to "${kind}".`,
    );
  }

  return (
    <VisualizerErrorBoundary algorithmId={algorithmId}>
      {/*
       * The `key` here is the single mechanism that guarantees full context
       * teardown. React will unmount the old VisualizerShell tree (running
       * every cleanup inside SortingVisualizer / GraphVisualizer / etc.) and
       * mount a fresh one whenever algorithmId changes.
       */}
      <div
        role="img"
        aria-label={`Algorithm visualization for ${algorithmId}`}
        aria-live="polite"
        className="h-full w-full"
      >
        <VisualizerShell
          key={algorithmId}
          currentStep={currentStep}
          kind={kind}
          algorithmId={algorithmId}
          algorithmType={algorithmType}
          mismatch={mismatch}
          allSteps={allSteps}
          stepIndex={stepIndex}
        />
      </div>
    </VisualizerErrorBoundary>
  );
});
